/* eslint-disable max-lines-per-function */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import type FrappeComponentIFrameWrapper from '@/components/iframe/FrappeComponentIFrameWrapper.vue'
import type {
  HasAlternativesResponse,
  HasRejectedItemsResponse,
  MapQuotationToSalesOrderPayload,
  QuotationSalesOrderContinuePayload,
  SelectedFrappeRow,
} from '@/types/components/iframe/types'
import type { DatevExportFilters } from '@/types/datev'

import { routeSupplierQuotationCreate } from '@/routers/routes/mainRoutes/routeBuying/supplierQuotationRoutes'
import { routeSalesOrderCreate } from '@/routers/routes/mainRoutes/routeSales/salesOrdersRoutes'
import { resolveDoctypeDetailRoute } from '@/utilities/doctypeRoutesHelper'

interface QueryReportExportOptions {
  fileFormat?: 'Excel' | 'PDF'
  includeFilters?: boolean
  orientation?: 'Landscape' | 'Portrait'
  withLetterHead?: boolean
}

interface ToolbarButtonState {
  action?: string
  disabled?: boolean
  label?: string
  primary?: boolean
}

interface ToolbarStateResponse {
  customButtons?: Record<string, ToolbarButtonState | undefined>
}

interface DataImportPrimaryActionState {
  canExportErroredRows: boolean
  disabled: boolean
  isRunning: boolean
  label: string
  processedRecords: number
  status: string
  totalRecords: number
  visible: boolean
}

interface DataImportPrimaryActionResponse {
  ok: boolean
}

interface DataImportCorrectionResponse extends DataImportPrimaryActionResponse {
  name?: string
}

const EMPTY_DATA_IMPORT_PRIMARY_ACTION_STATE: DataImportPrimaryActionState = {
  canExportErroredRows: false,
  disabled: true,
  isRunning: false,
  label: '',
  processedRecords: 0,
  status: '',
  totalRecords: 0,
  visible: false,
}

// Document actions that open a dialog of their own before anything happens
const ACTIONS_ASKING_FIRST = ['cancelDoc', 'submitDoc']

// How long a closed dialog still counts as "waiting for the user". A declined one ends the action
// right after, and without this grace its button would flash its spinner on the way out.
const SPINNER_RESUME_DELAY = 600

const isFailedActionResponse = (response: unknown): boolean => {
  return (
    typeof response === 'object' && response !== null && 'ok' in response && response.ok === false
  )
}

// eslint-disable-next-line max-statements
const useReportIframeStore = defineStore('ReportIframeStore', () => {
  const router = useRouter()
  const wrapperRef = ref<InstanceType<typeof FrappeComponentIFrameWrapper>>()
  const selectedItems = ref<SelectedFrappeRow[]>([])

  const isPrintView = ref<boolean>(false)
  const isTotalsActive = ref<boolean>(false)
  const pendingDocDetails = ref<null | {
    docname: string
    doctype: string
    likedByUser: boolean
    status: string
    title: string
    workflowState: string
  }>(null)
  const isFormDirty = ref<boolean>(false)
  const isDocSubmitted = ref<boolean>(false)
  // Reported by the iframe as `docstatus === 2`: the status text alone cannot tell, it is
  // translated and the translation differs per site
  const isDocCancelled = ref<boolean>(false)
  const isIFrameModalOpen = ref<boolean>(false)
  // A modal of the app itself is open in front of the document, e.g. a signature request. Views
  // report it, so the action area behaves the same as behind a dialog of the embedded form.
  const isHostModalOpen = ref<boolean>(false)
  const isDocSubmittable = ref<boolean>(false)
  const isDocStateLoaded = ref<boolean>(false)
  const docStatus = ref<string>('')
  const workflowState = ref<string | undefined>('')
  const docTitle = ref<string>('')
  const isLoading = ref<boolean>(false)
  // A save, update or submit is running in the iframe and has not reported back yet
  const isDocActionPending = ref<boolean>(false)
  // Which one it is, so the views keep showing the button that started it while it runs
  const pendingDocAction = ref<null | string>(null)
  // A dialog in the embedded form is waiting for an answer: the action is on the user now, not on
  // the system, so the buttons stop spinning while it is open
  const isAwaitingUserInput = ref<boolean>(false)
  let spinnerResumeTimer: number | undefined = undefined

  const awaitUserInput = (): void => {
    window.clearTimeout(spinnerResumeTimer)
    isAwaitingUserInput.value = true
  }

  /**
   * Ends the waiting state, immediately when the action is over and after a short grace when only
   * a dialog closed: what follows a closed dialog is either the work it triggered, which then
   * spins, or nothing at all, which must not.
   */
  const stopAwaitingUserInput = (withGrace = false): void => {
    window.clearTimeout(spinnerResumeTimer)

    if (!withGrace) {
      isAwaitingUserInput.value = false
      return
    }

    spinnerResumeTimer = window.setTimeout(() => {
      isAwaitingUserInput.value = false
    }, SPINNER_RESUME_DELAY)
  }
  const isDisabled = ref<boolean>(false)
  const areDocActionsBlocked = computed(
    () => isLoading.value || isDocActionPending.value || isHostModalOpen.value,
  )
  const isInstanceFormCustomizerOpen = ref<boolean>(false)
  const isInstanceFormCustomizerTransitioning = ref<boolean>(false)
  const isQueryReportDataReady = ref<boolean>(false)
  const dataImportPrimaryActionState = ref<DataImportPrimaryActionState>({
    ...EMPTY_DATA_IMPORT_PRIMARY_ACTION_STATE,
  })
  const iframeEventWrappers = new WeakSet<InstanceType<typeof FrappeComponentIFrameWrapper>>()
  const queryReportEventWrappers = new WeakSet<InstanceType<typeof FrappeComponentIFrameWrapper>>()

  const unsavedFormData = ref<Record<string, { formData: object; timestamp: number }>>({})
  const removedUnsavedFormDataIds = ref<Set<string>>(new Set())

  const registerGlobalEvents = (): void => {
    window.addEventListener('message', (event) => {
      // navigating when e invoice import upload is complete
      if (event.data?.event === 'eInvoiceImportDetailPageLoaded' && event.data?.docname) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push(`/accountings/e-invoice-imports/${event.data.docname}`)
      }

      // Open doctype detail page in a new tab when the event is triggered from the iframe
      if (event.data?.event === 'openInNewTab' && event.data?.doctype && event.data?.docname) {
        const route = resolveDoctypeDetailRoute(router, event.data.doctype, event.data.docname)
        if (route) {
          window.open(router.resolve(route).href, '_blank')
        }
      }
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const sendToIframe = <T = unknown>(
    iframeEvent: string,
    message: unknown,
    iframeCallback?: (value: T) => Promise<void> | void,
  ): void => {
    if (wrapperRef.value) {
      wrapperRef.value
        .send<T>(iframeEvent, message)
        .then(async (resp: T) => {
          if (iframeCallback) {
            await iframeCallback(resp)
          }
        })
        .catch((frappeError: unknown) => {
          console.debug(frappeError)
        })
    }
  }

  /**
   * typed + awaitable wrapper around wrapperRef.send(...)
   */
  const sendToIframeAsync = async <T>(iframeEvent: string, message: unknown): Promise<T> => {
    if (!wrapperRef.value) {
      throw new Error(`[ReportIframeStore] wrapperRef not set for event "${iframeEvent}"`)
    }

    return await wrapperRef.value.send<T>(iframeEvent, message)
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const triggerIframeAction = <T = unknown>(
    iframeEvent: string,
    message: unknown,
    iframeCallback?: (value: T) => Promise<void> | void,
  ): void => {
    isLoading.value = true
    sendToIframe<T>(iframeEvent, message, iframeCallback)
  }
  // Form actions (TODO: migrate to own store?)
  const deleteFileAttachment = (
    fid: string,
    iframeCallback?: (value: unknown) => Promise<void> | void,
  ): void => {
    triggerIframeAction('openDeleteFileDialog', { fid }, iframeCallback)
  }

  const deleteCurrentDoc = (): void => {
    triggerIframeAction('deleteCurrentDoc', {})
  }

  const printDocPreview = (): void => {
    triggerIframeAction('printPreviewDoc', {})
    isPrintView.value = true
  }

  const closePrintDocPreview = (): void => {
    triggerIframeAction('openFormView', {})
    isPrintView.value = false
    isLoading.value = false
  }

  const printDocAsPdf = (): void => {
    triggerIframeAction('printPdfDoc', {})
  }

  const openEmailModal = (): void => {
    triggerIframeAction('openEmailModal', {})
  }

  const getToolbarState = async (): Promise<ToolbarStateResponse> => {
    return await sendToIframeAsync<ToolbarStateResponse>('getToolbarState', {})
  }

  // Fetches the current primary action state for the given document.
  const refreshDataImportPrimaryActionState = async (expectedDocname: string): Promise<void> => {
    dataImportPrimaryActionState.value = await sendToIframeAsync<DataImportPrimaryActionState>(
      'getDataImportPrimaryActionState',
      { docname: expectedDocname },
    )
  }

  const resetDataImportPrimaryActionState = (): void => {
    dataImportPrimaryActionState.value = { ...EMPTY_DATA_IMPORT_PRIMARY_ACTION_STATE }
  }

  const triggerDataImportPrimaryAction = async (docname: string): Promise<void> => {
    isLoading.value = true
    dataImportPrimaryActionState.value = {
      ...dataImportPrimaryActionState.value,
      disabled: true,
      isRunning: true,
    }

    try {
      await sendToIframeAsync<DataImportPrimaryActionResponse>('triggerDataImportPrimaryAction', {})
      await refreshDataImportPrimaryActionState(docname)
    } finally {
      isLoading.value = false
    }
  }

  const exportDataImportErroredRows = async (fileType: 'CSV' | 'Excel' = 'CSV'): Promise<void> => {
    isLoading.value = true

    try {
      await sendToIframeAsync<DataImportPrimaryActionResponse>('exportDataImportErroredRows', {
        fileType,
      })
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Creates a data import with the settings of the current failed one and
   * returns its name, so the corrected rows run as a separate import.
   */
  const createDataImportCorrection = async (): Promise<string | undefined> => {
    isLoading.value = true

    try {
      const response = await sendToIframeAsync<DataImportCorrectionResponse>(
        'createDataImportCorrection',
        {},
      )
      return response.ok ? response.name : undefined
    } finally {
      isLoading.value = false
    }
  }

  const reloadCurrentIframe = async (): Promise<void> => {
    isFormDirty.value = false
    // Whatever was running is gone with the old iframe content
    isDocActionPending.value = false
    pendingDocAction.value = null
    await wrapperRef.value?.reloadIframe()
  }

  const openInstanceFormCustomizer = async (doctype: string, docname?: string): Promise<void> => {
    isLoading.value = true
    isInstanceFormCustomizerTransitioning.value = true

    try {
      await reloadCurrentIframe()
      const response = await sendToIframeAsync('openInstanceFormCustomizer', { docname, doctype })
      if (isFailedActionResponse(response)) {
        isInstanceFormCustomizerOpen.value = false
        return
      }

      await wrapperRef.value?.waitUntilInstanceFormCustomizerReady()
      isInstanceFormCustomizerOpen.value = true
      isFormDirty.value = false
    } finally {
      isInstanceFormCustomizerTransitioning.value = false
      isLoading.value = false
    }
  }

  const closeInstanceFormCustomizer = async (): Promise<void> => {
    isLoading.value = true
    isInstanceFormCustomizerTransitioning.value = true

    try {
      await sendToIframeAsync('closeInstanceFormCustomizer', {})
      isInstanceFormCustomizerOpen.value = false
      await reloadCurrentIframe()
    } finally {
      isInstanceFormCustomizerTransitioning.value = false
      isLoading.value = false
    }
  }

  const resetInstanceFormCustomizerState = (): void => {
    isInstanceFormCustomizerOpen.value = false
    isInstanceFormCustomizerTransitioning.value = false
  }

  /**
   * Runs a document action in the iframe (saving, submitting, cancelling, amending, closing and
   * reopening) and keeps `isDocActionPending` set until the iframe reports the outcome.
   * `isLoading` alone cannot gate these buttons: any modal closing in the iframe (the submit
   * confirmation, the document chain dialog) resets it while the request is still running, which
   * is what allowed the same action to run several times on one document.
   * `onAnswer` receives the answer of the iframe, it is not called when none arrives.
   */
  const runDocAction = (iframeEvent: string, onAnswer?: (response: unknown) => void): void => {
    if (isDocActionPending.value) return

    isDocActionPending.value = true
    pendingDocAction.value = iframeEvent
    // These always ask before they do anything, so the waiting starts with the user and the
    // button does not spin for the moment it takes the dialog to appear
    if (ACTIONS_ASKING_FIRST.includes(iframeEvent)) {
      awaitUserInput()
    } else {
      stopAwaitingUserInput()
    }
    isLoading.value = true

    sendToIframeAsync(iframeEvent, {})
      .then((response: unknown) => {
        onAnswer?.(response)
      })
      .catch((frappeError: unknown) => {
        console.debug(frappeError)
      })
      .finally(() => {
        // Both states end with the answer: `isLoading` is otherwise only reset by a closing
        // dialog or the next document state, which leaves the buttons disabled while Frappe's
        // message about a refused save is still on screen
        isDocActionPending.value = false
        pendingDocAction.value = null
        stopAwaitingUserInput()
        isLoading.value = false
      })
  }

  const saveDoc = (): void => {
    runDocAction('saveForm')
  }

  const saveOrUpdate = (): void => {
    runDocAction('saveOrUpdate')
  }

  const submitDoc = (): void => {
    runDocAction('submitDoc')
  }

  /**
   * Submits like `submitDoc` and calls `onSubmitted` once the iframe answers that the document is
   * booked.
   */
  const submitDocThen = (onSubmitted: () => void): void => {
    runDocAction('submitDoc', (response) => {
      if (response === true) onSubmitted()
    })
  }
  const connectApp = (): void => {
    triggerIframeAction(
      'connectApp',
      {
        success_uri: window.location.href,
      },
      () => {
        isLoading.value = false
      },
    )
  }

  const fetchOpenId = (): void => {
    triggerIframeAction('fetchOpenId', {}, () => {
      isLoading.value = false
    })
  }

  const authorizeApi = (): void => {
    triggerIframeAction(
      'authorizeApi',
      {
        success_uri: window.location.href,
      },
      () => {
        isLoading.value = false
      },
    )
  }

  const relinkDoc = (): void => {
    triggerIframeAction('relinkDoc', {})
  }

  const closeDoc = (): void => {
    runDocAction('closeDocument')
  }

  /**
   * Reopens a closed document. ERPNext labels that toolbar button differently per doctype, which
   * the iframe handler resolves, so one action covers every doctype.
   */
  const reopenDoc = (): void => {
    runDocAction('reopenDocument')
  }

  const sendNewsletterArticleNow = (): void => {
    triggerIframeAction('sendNewsletterArticleNow', {})
  }

  const planNewsletterArticle = (): void => {
    triggerIframeAction('planNewsletterArticle', {})
  }

  const sendTestNewsletterArticle = (): void => {
    triggerIframeAction('sendTestNewsletterArticle', {})
  }

  const checkNewsletterArticleLinks = (): void => {
    triggerIframeAction('checkNewsletterArticleLinks', {}, () => {
      isLoading.value = false
    })
  }

  const addSubscriber = (): void => {
    triggerIframeAction('addSubscriber', {})
  }

  // Bulk actions for selected items in the report iframe
  const exportItems = (): void => {
    triggerIframeAction('exportItems', selectedItems.value)
  }

  const triggerIframeModalAction = (iframeEvent: string): void => {
    isLoading.value = true
    isIFrameModalOpen.value = true

    sendToIframeAsync(iframeEvent, selectedItems.value)
      .catch((frappeError: unknown) => {
        console.debug(frappeError)
        isIFrameModalOpen.value = false
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const bulkEdit = (): void => {
    triggerIframeModalAction('bulkEdit')
  }

  const bulkDelete = (): void => {
    triggerIframeModalAction('bulkDelete')
  }

  const bulkAddTag = (): void => {
    triggerIframeModalAction('bulkAddTag')
  }

  const markAsRead = (): void => {
    triggerIframeAction('markAsRead', selectedItems.value, () => {
      selectedItems.value = []
      isLoading.value = false
    })
  }

  const markAsUnread = (): void => {
    triggerIframeAction('markAsUnread', selectedItems.value, () => {
      selectedItems.value = []
      isLoading.value = false
    })
  }

  const exportListAll = (): void => {
    triggerIframeAction('exportListAll', selectedItems.value)
  }

  /**
   * Cancels a document of any type.
   */
  const cancelDoc = (): void => {
    runDocAction('cancelDoc')
  }

  /**
   * Amend function: creates a new draft
   */
  const amendDoc = (): void => {
    runDocAction('amendDoc')
  }

  /**
   * Opens the upload modal for a specific doctype and docname.
   */
  const openUploadModal = (doctype: string, docname: string): void => {
    triggerIframeAction('openUploadModal', { docname, doctype })
    isIFrameModalOpen.value = true
  }

  const openAssignmentModal = (): void => {
    sendToIframe('openAssignmentModal', {})
    isIFrameModalOpen.value = true
  }

  /**
   * quotationHasAlternatives / quotationShowAlternativeItemsDialog: quotation-specific helpers used by the views
   */
  const quotationHasAlternatives = async (): Promise<boolean> => {
    const resp = await sendToIframeAsync<HasAlternativesResponse>('hasAlternatives', {})
    return resp.hasAlternatives
  }

  const quotationShowAlternativeItemsDialog = async (): Promise<void> => {
    await sendToIframeAsync('showAlternativeItemsDialog', {})
  }

  /**
   * persists modal selection in sessionStorage and routes to Sales Order create
   */
  const handleQuotationSalesOrderContinue = async (
    payload: QuotationSalesOrderContinuePayload,
  ): Promise<void> => {
    // Remove old altkeys
    for (let element = sessionStorage.length - 1; element >= 0; element--) {
      const oldKeys = sessionStorage.key(element)
      if (oldKeys?.startsWith('so_alt_')) sessionStorage.removeItem(oldKeys)
    }

    const key = `so_alt_${Date.now()}_${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem(key, JSON.stringify(payload))

    await router.push({
      name: routeSalesOrderCreate.name,
      query: { altKey: key, docname: payload.quotation, doctype: 'Quotation' },
    })
  }

  /**
   * triggers ERPNext mapping from Quotation -> Sales Order
   */
  const mapQuotationToSalesOrder = async (
    payload: MapQuotationToSalesOrderPayload,
  ): Promise<void> => {
    await sendToIframeAsync('mapQuotationToSalesOrder', payload)
  }

  /**
   * Route to the Supplier Quotation creation page after selecting a supplier in the ERPNext modal
   */
  const makeSupplierQuotationFromRequestForQuotation = async (payload: {
    docname: string
    supplier: string
  }): Promise<void> => {
    await router.push({
      name: routeSupplierQuotationCreate.name,
      query: {
        docname: payload.docname,
        doctype: 'Request for Quotation',
        supplier: payload.supplier,
      },
    })
  }

  // Event handling
  const registerFormEvents = (): void => {
    if (wrapperRef.value) {
      wrapperRef.value.events.on('formDirtyState', (response: boolean) => {
        isFormDirty.value = response
      })

      wrapperRef.value.events.on(
        'dataImportPrimaryActionState',
        (response: DataImportPrimaryActionState) => {
          dataImportPrimaryActionState.value = response
        },
      )

      wrapperRef.value.events.on(
        'docStates',
        (payload: {
          disabled?: boolean
          isCancelled?: boolean
          isSubmittable: boolean
          isSubmitted: boolean
          status: string
          title: string
          workflowState?: string
        }) => {
          isDocSubmitted.value = payload.isSubmitted
          isDocCancelled.value = payload.isCancelled ?? false
          docStatus.value = payload.status
          workflowState.value = payload.workflowState
          docTitle.value = payload.title
          isDocSubmittable.value = payload.isSubmittable
          isPrintView.value = false
          isFormDirty.value = false
          isLoading.value = false
          // Safety net: the iframe may be replaced before the running action answers
          isDocActionPending.value = false
          pendingDocAction.value = null
          stopAwaitingUserInput()
          isDisabled.value = payload.disabled ?? false
          isDocStateLoaded.value = true
        },
      )

      wrapperRef.value.events.on('dialogOpened', () => {
        awaitUserInput()
      })

      wrapperRef.value.events.on('dialogClosed', () => {
        stopAwaitingUserInput(true)
        isLoading.value = false
      })

      wrapperRef.value.events.on('formModalOpen', () => {
        isLoading.value = true
      })

      // Listen for the custom event triggered after selecting a supplier in the ERPNext modal from a Request for Quotation
      wrapperRef.value.events.on(
        'makeSupplierQuotationFromRequestForQuotation',
        async (payload: { docname: string; supplier: string }) => {
          await makeSupplierQuotationFromRequestForQuotation(payload)
        },
      )
    }
  }

  const registerUploadEvent = (cb: (event: { success: boolean }) => Promise<void> | void): void => {
    if (!wrapperRef.value) return
    wrapperRef.value.events.on('uploadResult', cb)
  }
  const registerDeleteEvent = (cb: (event: { success: boolean }) => Promise<void> | void): void => {
    if (!wrapperRef.value) return
    wrapperRef.value.events.on('deleteFileResult', cb)
  }

  const syncSelectedItemsWithCount = (itemsLength?: number): void => {
    if (typeof itemsLength !== 'number' || itemsLength <= 0) {
      selectedItems.value = []
      return
    }

    if (selectedItems.value.length > itemsLength) {
      selectedItems.value = selectedItems.value.slice(0, itemsLength)
    }
  }

  const registerEvents = (): void => {
    const currentWrapper = wrapperRef.value
    if (!currentWrapper || iframeEventWrappers.has(currentWrapper)) return

    iframeEventWrappers.add(currentWrapper)

    currentWrapper.events.on('totalsState', (event: { active: boolean }) => {
      isTotalsActive.value = event.active
    })
    currentWrapper.events.on('selectedItems', (event: { items: SelectedFrappeRow[] }) => {
      selectedItems.value = event.items
    })
    currentWrapper.events.on('modalClosed', (event: { itemsLength: number }) => {
      syncSelectedItemsWithCount(event.itemsLength)
      isIFrameModalOpen.value = false
      isLoading.value = false
    })
    currentWrapper.events.on('dialogOpened', () => {
      awaitUserInput()
    })

    currentWrapper.events.on('dialogClosed', () => {
      stopAwaitingUserInput(true)
      isIFrameModalOpen.value = false
      isLoading.value = false
    })

    currentWrapper.events.on(
      'quotationSalesOrderContinue',
      async (payload: QuotationSalesOrderContinuePayload) => {
        await handleQuotationSalesOrderContinue(payload)
      },
    )
  }

  const registerQueryReportEvents = (): void => {
    const currentWrapper = wrapperRef.value
    if (!currentWrapper || queryReportEventWrappers.has(currentWrapper)) return

    queryReportEventWrappers.add(currentWrapper)
    isQueryReportDataReady.value = false

    currentWrapper.events.on('toggleButtonDisabled', (event: { disabled: boolean }) => {
      isLoading.value = event.disabled
    })
    currentWrapper.events.on('queryReportDataState', (event: { ready: boolean }) => {
      isQueryReportDataReady.value = event.ready
    })
  }

  /**
   * Retrieves the current form data from the iframe
   */
  const getCurrentFormData = async (): Promise<object> => {
    return await new Promise((resolve, reject) => {
      sendToIframe('getFormData', {}, (resp) => {
        if (resp) resolve(resp)
        else reject(new Error('No form data returned'))
      })
    })
  }

  /**
   * Remove unsaved form data entries older than 30 minutes
   */
  const cleanupOldUnsavedFormData = (maxAgeMs = 1800000): void => {
    const now = Date.now()

    Object.keys(unsavedFormData.value).forEach((key) => {
      const entry = unsavedFormData.value[key]
      if (entry && now - entry.timestamp > maxAgeMs) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete unsavedFormData.value[key]
      }
    })
  }

  /**
   * Save unsaved form data for a given unsavedFormDataId
   */
  const setUnsavedFormData = (unsavedFormDataId: string, formData: object): void => {
    // Skip saving if unsavedFormDataId was marked as removed
    if (removedUnsavedFormDataIds.value.has(unsavedFormDataId)) return

    unsavedFormData.value[unsavedFormDataId] = {
      formData,
      timestamp: Date.now(),
    }
  }

  /**
   * Get unsaved form data for a given unsavedFormDataId
   */
  const getUnsavedFormData = (unsavedFormDataId: string): null | object => {
    // Clean up old unsaved form data entries before retrieving
    cleanupOldUnsavedFormData()

    const entry = unsavedFormData.value[unsavedFormDataId]

    return entry ? entry.formData : null
  }

  /**
   * Delete unsaved form data for a given unsavedFormDataId and mark as cleaned up
   */
  const deleteUnsavedFormData = (unsavedFormDataId: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete unsavedFormData.value[unsavedFormDataId]
    removedUnsavedFormDataIds.value.add(unsavedFormDataId)
  }

  /**
   * Reset OTP secret for a user
   */
  const resetOtpSecret = (): void => {
    triggerIframeAction('resetOtpSecret', {})
  }

  /**
   * Toggles the totals row in the report view
   */
  const toggleTotals = (): void => {
    triggerIframeAction<{ active: boolean }>('toggleTotals', {}, (resp) => {
      isTotalsActive.value = resp.active
      isLoading.value = false
    })
  }

  /**
   * Triggers query report generation in the iframe
   */
  const generateReport = (): void => {
    triggerIframeAction('generateReport', {})
  }

  /**
   * Triggers query report export in the iframe
   */
  const exportReport = async (options: QueryReportExportOptions = {}): Promise<void> => {
    await sendToIframeAsync('exportReport', options)
  }

  /**
   * Retrieves the filters currently selected in the DATEV query report.
   */
  const getDatevExportFilters = async (): Promise<DatevExportFilters> => {
    return await sendToIframeAsync<DatevExportFilters>('getDatevExportFilters', {})
  }

  /**
   * Checks if the current purchase receipt has rejected items
   */
  const hasRejectedItems = async (): Promise<boolean> => {
    const resp = await sendToIframeAsync<HasRejectedItemsResponse>('hasRejectedItems', {})
    return resp.hasRejectedItems
  }

  /**
   * Opens a modal so send a Request for Quotation to suppliers
   */
  const sendEmailToSuppliers = async (): Promise<void> => {
    await sendToIframeAsync('sendEmailToSuppliers', {})
  }

  /**
   * Opens a modal to download the Request for Quotation as PDF
   */
  const downloadRequestForQuotation = async (): Promise<void> => {
    await sendToIframeAsync('downloadRequestForQuotation', {})
  }

  /**
   * Opens a modal to create a Supplier Quotation from a Request for Quotation
   */
  const createSupplierQuotation = async (): Promise<void> => {
    await sendToIframeAsync('createSupplierQuotation', {})
  }

  /**
   * Opens a modal to update items in a Purchase Order
   */
  const updateItemsInPurchaseOrder = async (): Promise<void> => {
    await sendToIframeAsync('updateItemsInPurchaseOrder', {})
  }

  /**
   * Activates a warehouse group
   */
  const activateWarehouseGroup = async (): Promise<void> => {
    await sendToIframeAsync('activateWarehouseGroup', {})
  }

  /**
   * Deactivates a warehouse group
   */
  const deactivateWarehouseGroup = async (): Promise<void> => {
    await sendToIframeAsync('deactivateWarehouseGroup', {})
  }

  /**
   * Sends a Datev mail
   */
  const sendDatevMail = async (): Promise<void> => {
    await sendToIframeAsync('sendDatevMail', {})
  }

  registerGlobalEvents()

  return {
    activateWarehouseGroup,
    addSubscriber,
    amendDoc,
    areDocActionsBlocked,
    authorizeApi,
    bulkAddTag,
    bulkDelete,
    bulkEdit,
    cancelDoc,
    checkNewsletterArticleLinks,
    cleanupOldUnsavedFormData,
    closeDoc,
    closeInstanceFormCustomizer,
    closePrintDocPreview,
    connectApp,
    createDataImportCorrection,
    createSupplierQuotation,
    dataImportPrimaryActionState,
    deactivateWarehouseGroup,
    deleteCurrentDoc,
    deleteFileAttachment,
    deleteUnsavedFormData,
    docStatus,
    docTitle,
    downloadRequestForQuotation,
    exportDataImportErroredRows,
    exportItems,
    exportListAll,
    exportReport,
    fetchOpenId,
    generateReport,
    getCurrentFormData,
    getDatevExportFilters,
    getToolbarState,
    getUnsavedFormData,
    hasRejectedItems,
    isAwaitingUserInput,
    isDisabled,
    isDocActionPending,
    isDocCancelled,
    isDocStateLoaded,
    isDocSubmittable,
    isDocSubmitted,
    isFormDirty,
    isHostModalOpen,
    isIFrameModalOpen,
    isInstanceFormCustomizerOpen,
    isInstanceFormCustomizerTransitioning,
    isLoading,
    isPrintView,
    isQueryReportDataReady,
    isTotalsActive,
    mapQuotationToSalesOrder,
    markAsRead,
    markAsUnread,
    openAssignmentModal,
    openEmailModal,
    openInstanceFormCustomizer,
    openUploadModal,
    pendingDocAction,
    pendingDocDetails,
    planNewsletterArticle,
    printDocAsPdf,
    printDocPreview,
    quotationHasAlternatives,
    quotationShowAlternativeItemsDialog,
    refreshDataImportPrimaryActionState,
    registerDeleteEvent,
    registerEvents,
    registerFormEvents,
    registerGlobalEvents,
    registerQueryReportEvents,
    registerUploadEvent,
    relinkDoc,
    reloadCurrentIframe,
    removedUnsavedFormDataIds,
    reopenDoc,
    resetDataImportPrimaryActionState,
    resetInstanceFormCustomizerState,
    resetOtpSecret,
    saveDoc,
    saveOrUpdate,
    selectedItems,
    sendDatevMail,
    sendEmailToSuppliers,
    sendNewsletterArticleNow,
    sendTestNewsletterArticle,
    sendToIframe,
    sendToIframeAsync,
    setUnsavedFormData,
    submitDoc,
    submitDocThen,
    toggleTotals,
    triggerDataImportPrimaryAction,
    triggerIframeAction,
    unsavedFormData,
    updateItemsInPurchaseOrder,
    workflowState,
    wrapperRef,
  }
})

export { useReportIframeStore }
